import React, { useCallback, useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import client from "../../api";
import { getErrorMessage, logger } from "src/lib/utils";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import GlobalContext from "src/context/global-context";
import {
  BillingServiceItem,
  IcasinoService,
  moneyMoversCategory,
  MoneyMoverServiceCategory,
  PaginationInput,
} from "src/api/types";
import { Table } from "antd";
import moment from "moment";
import { ColumnsType } from "antd/es/table";
import { PlusIcon } from "../icons/plus-icon";
import AdminFormPage from "../admins/admin-form";
import CategoryFormPage from "./addCategoryForm";
import { EditIcon } from "../icons/edit-icon";
import { DeleteIcon } from "../icons/delete-icon";
import { useModalAction } from "../modal-views/context";
import { setTimeout } from "timers/promises";
import { useNavigate } from "react-router-dom";
import routes from "src/config/routes";
import ServiceFormPage from "./addServiceForm";
import { callback } from "chart.js/dist/helpers/helpers.core";
import { TransactionHistoryIcon } from "../icons/transaction-history-icon";
import SearchTab from "../../../src/components/search-filters/basic-search-filter";
import debounce from "lodash.debounce";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";

const Services = () => {
  const navigate = useNavigate();
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const { canView, canList, canAdd, canEdit, canDelete } = usePermissions(
    permissionConstants.MONEY_MOVER_SERVICES
  );
  const canViewServices = canView || canList;
  const [total, setTotal] = useState(0);
  const [dataSource, setDataSource] = useState<IcasinoService[]>();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useState<PaginationInput>({
    per_page: LIST_PAGE_SIZE,
    current_page: 1,
  });
  const [searchText, setSearchText] = useState("");
  const { openModal } = useModalAction();

  const { mutate: getList, isLoading: listLoading } = useMutation(
    client.moneyMover.servicesList,
    {
      onSuccess: (data: any) => {
        setTotal(data.data.total);
        let details = data.data.data;
        setDataSource(
          details.map((item: any) => ({
            key: item.id,
            service_image: item.service_image,
            service_name: item.service_name,
            is_accurrencytive: item.currency,
            is_active: item.is_active,
            cat_name: item.service_category.cat_name,
            cat_image: item.service_category.cat_image,
            currency: item.currency ? item.currency.split("|")[1] : "",
            catId: item.cat_id,
            max_amount: item.max_amount,
            min_amount: item.min_amount,
          }))
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  const debouncedSearch = useCallback(
    debounce((value: any) => getList({ ...value }), 500),
    [] // empty dependency array to keep the debounce instance stable
  );

  useEffect(() => {
    if (!canViewServices) return;
    if (searchText) {
      debouncedSearch({ ...searchParams, search_value: searchText });
    } else {
      getList({ ...searchParams });
    }
  }, [searchParams, searchText, canViewServices]);

  const [isNew, setIsNew] = useState(false);
  const [editingKey, setEditingKey] = useState(-1);

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 768 ? true : false);
  }

  const onPageChange = (page: number, pageSize: number) => {
    logger.log(page, pageSize);
    setSearchParams({ ...searchParams, current_page: page });
  };

  const onDelete = (key: number) => {
    openModal("SERVICE_DELETE_CONFIRM_VIEW", {
      id: key,
      callback: onCallback,
    });
  };

  const onEdit = (key: number) => {
    logger.log("edit");
    setIsNew(false);
    setEditingKey(key);
  };

  useEffect(() => {
    setSearchParams({
      per_page: LIST_PAGE_SIZE,
      current_page: 1,
    });
    setSearchText("");
  }, [isNew, editingKey]);
  
  const onCategoryUpdate = (key: number, catId: number) => {
    logger.log("edit");
    openModal("SERVICE_CATEGORY_VIEW", {
      id: key,
      callback: onCallback,
      selectedId: catId,
    });
  };

  const columns: ColumnsType<IcasinoService> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Category Name
        </p>
      ),
      dataIndex: "cat_name",
      className: "w-auto",
      key: "cat_name",
      render: (value, record: IcasinoService) => (
        <div className="flex flex-row gap-2 items-center ">
          <div className="flex-shrink-0 h-[48px] w-[48px] rounded-full bg-[#e0e0e0] flex items-center justify-center">
            <img
              src={record?.cat_image} // ensure this is a valid image URL
              alt="image"
              className="w-[45px] h-[45px] object-contain rounded"
            />
          </div>
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value[2]}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Service Name
        </p>
      ),
      dataIndex: "service_name",
      className: "w-auto",
      key: "service_name",
      render: (value, record: IcasinoService) => (
        <div className="flex flex-row gap-2 items-center ">
          <div className="flex-shrink-0 h-[48px] w-[48px] rounded-full bg-[#e0e0e0] flex items-center justify-center">
            <img
              src={record?.service_image} // ensure this is a valid image URL
              alt="image"
              className="w-[45px] h-[45px] object-contain rounded"
            />
          </div>
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value[2]}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Status{" "}
        </p>
      ),
      dataIndex: "is_active",
      className: "w-auto",
      key: "is_active",
      render: (value, record: IcasinoService) => (
        <div className="flex flex-row gap-2 items-center ">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value === "Y" ? "Yes" : "No"}{" "}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Min Amount{" "}
        </p>
      ),
      dataIndex: "min_amount",
      className: "w-auto",
      key: "min_amount",
      render: (value, record: IcasinoService) => (
        <div className="flex flex-row gap-2 items-center ">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Max Amount{" "}
        </p>
      ),
      dataIndex: "max_amount",
      className: "w-auto",
      key: "max_amount",
      render: (value, record: IcasinoService) => (
        <div className="flex flex-row gap-2 items-center ">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Action
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_, record: IcasinoService) => {
        if (!canEdit && !canDelete) {
          return <span className="text-secondary text-[13px]">—</span>;
        }
        return (
          <div className="flex flex-row">
            {canEdit && (
              <>
                <button
                  className="w-[30px] h-[30px] flex items-center justify-center"
                  onClick={() =>
                    onCategoryUpdate(record.key as number, record.catId as number)
                  }
                >
                  <TransactionHistoryIcon className="w-[20px] h-[20px] text-primary" />
                </button>
                <button
                  className="w-[30px] h-[30px] flex items-center justify-center"
                  onClick={() => onEdit(record.key as number)}
                >
                  <EditIcon className="w-[20px] h-[20px] text-primary" />
                </button>
              </>
            )}
            {canDelete && (
              <button
                className="w-[30px] h-[30px] flex items-center justify-center"
                onClick={() => onDelete(record.key as number)}
              >
                <DeleteIcon className="w-[20px] h-[20px] text-primary" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const mobileColumns: ColumnsType<IcasinoService> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Service Name
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_, record: { key: React.Key }) => {
        const selData = dataSource?.filter(
          (item) => item.key === record.key
        )[0];

        return (
          <div className="w-full flex flex-row gap-2 justify-between">
            <div className="w-full flex flex-col items-start gap-2">
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Service
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData?.service_name}
                </p>
              </div>

              {/* <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Time
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.created_at || "---"}
                </p>
              </div> */}
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Status
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {/* {selData.status || "---"} */}
                </p>
              </div>
            </div>
          </div>
        );
      },
    },
  ];

  const onAdd = () => {
    setIsNew(true);
    setEditingKey(-1);
  };

  const onCallback = (bSuccess: boolean) => {
    setIsNew(false);
    setEditingKey(-1);
    bSuccess && getList({ ...searchParams });
  };

  return (
    <div className="w-full h-full bg-gray">
      {isNew || editingKey !== -1 ? (
        <ServiceFormPage
          isNew={isNew}
          editingUser={
            editingKey === -1
              ? undefined
              : dataSource?.filter((item) => item.key === editingKey)[0]
          }
          onCallback={onCallback}
        />
      ) : (
        <div className="w-full h-full flex-col p-2 sm:p-4">
          <div className="w-full flex flex-row items-center justify-between">
            <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
              Service List
            </p>
            <div className="flex align-center gap-2">
              <div className="">
                <SearchTab
                  placeholder="Search...."
                  searchText={searchText}
                  setSearchText={setSearchText}
                />
              </div>
            </div>
            {canAdd && (
              <button
                className="flex flex-row items-center justify-between px-2 sm:px-4 py-2 gap-2 bg-white rounded-[3px] flex-shrink-0 hover:bg-[#E6E8EC]"
                onClick={onAdd}
              >
                <p className="font-manrope text-[14px] font-medium text-primary">
                  Add Service
                </p>
                <PlusIcon className="w-[18px] h-[18px] text-primary" />
              </button>
            )}
          </div>

          {!canViewServices ? (
            <div className="flex flex-col items-center justify-center bg-white rounded-[12px] mt-2 sm:mt-4 p-8 text-center shadow-sm">
              <p className="font-manrope text-[16px] font-semibold text-secondary">
                You do not have permission to view money mover services.
              </p>
            </div>
          ) : (
            <div className="flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
              <Table
                loading={listLoading}
                showSorterTooltip={false}
                columns={isMobile ? mobileColumns : columns}
                dataSource={dataSource}
                pagination={{
                  total,
                  current: searchParams.current_page,
                  onChange: onPageChange,
                  position: ["bottomCenter"],
                  defaultPageSize: LIST_PAGE_SIZE,
                  showSizeChanger: false,
                }}
                // pagination={false}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Services;
